import {
  CheckOutlined,
  AddToHomeScreen,
  ScreenLockPortrait,
  ClearOutlined,
} from "@mui/icons-material";
import { Box } from "@mui/material";
import type { StepIconProps } from "@mui/material";
import type { Theme } from "@mui/material/styles";
import type { SvgIconProps } from "@mui/material/SvgIcon";
import { Colors } from "Components/Layout/Themes/Colors";
import { UNSUCCESSFUL_STATUSES } from "Components/Merchant/InitiatePaymentDetail/PaymentProgressIndicator/constants";
import { MooneIcon } from "Components/Shared/Icons/MooneIcon";
import { useResponsiveValue } from "Hooks/useResponsiveValue";
import type { TransactionStatus } from "Infrastructure/Api/Api";
import type { ComponentType } from "react";

const getActiveStepColor = (
  palette: Theme["palette"],
  stepNumber: number,
  transactionStatus: TransactionStatus,
) => {
  const isUnsuccessful = UNSUCCESSFUL_STATUSES.includes(transactionStatus);

  const colorByStep: Record<number, string> = {
    1: palette.info.main,
    2: Colors.Purple,
    3: palette.warning.main,
    4: isUnsuccessful ? palette.error.main : palette.success.main,
  };

  return colorByStep[stepNumber] ?? palette.primary.main;
};

type Props = StepIconProps & {
  transactionStatus: TransactionStatus;
};

export const ProgressStepIcon = ({
  active,
  className,
  icon,
  transactionStatus,
}: Props) => {
  const getResponsiveValue = useResponsiveValue();

  const isUnsuccessfulState = UNSUCCESSFUL_STATUSES.includes(transactionStatus);
  const icons: Record<string, ComponentType<SvgIconProps>> = {
    1: AddToHomeScreen,
    2: MooneIcon,
    3: ScreenLockPortrait,
    4: isUnsuccessfulState ? ClearOutlined : CheckOutlined,
  };
  const stepNumber = Number(icon);
  const Icon = icons[String(icon)] ?? ClearOutlined;

  return (
    <Box
      aria-hidden
      className={className}
      sx={({ palette }) => ({
        width: [32, 48],
        height: [32, 48],
        borderRadius: "50%",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        top: 0,
        color: active ? palette.common.white : palette.text.disabled,
        bgcolor: active
          ? getActiveStepColor(palette, stepNumber, transactionStatus)
          : palette.grey[300],
        boxShadow: active
          ? `0 0 0 4px ${getActiveStepColor(palette, stepNumber, transactionStatus)}33`
          : "none",
      })}
    >
      <Icon fontSize={getResponsiveValue(["small", "medium"])} />
    </Box>
  );
};
